import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { redeemBetaCode } from '../lib/beta';
import { track } from '../utils/analytics';

// Saisie et validation d'un code beta. Le succes pose la row `purchase`/`beta`
// (via redeemBetaCode) : le miroir user emet, et toute l'app se deverrouille sans
// action supplementaire. Message d'erreur neutre : le serveur ne distingue pas
// code invalide / desactive / expire / cap atteint.
const BetaCodeDialog = ({ open, onClose, onSuccess }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      setCode('');
      setBusy(false);
      setError(null);
      setDone(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!code.trim() || busy) return;
    setBusy(true);
    setError(null);
    track('beta_code_submitted');
    const res = await redeemBetaCode(code);
    setBusy(false);
    if (res.success) {
      track('beta_code_redeemed');
      setDone(true);
      setTimeout(() => {
        onClose();
        if (onSuccess) onSuccess();
      }, 1600);
    } else if (res.error === 'network') {
      setError("Couldn't reach the server. Check your connection and try again.");
    } else {
      setError('That code is invalid or no longer active.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { mb: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))' } }}
    >
      {done ? (
        <Box sx={{ textAlign: 'center', px: 3, py: 4 }}>
          <CheckCircleIcon sx={{ fontSize: '3rem', color: '#4ade80', mb: 1 }} />
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
            You're in! The full app is unlocked.
          </Typography>
        </Box>
      ) : (
        <>
          <DialogTitle>Enter your invitation code</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Type the code from your invitation email to unlock the full app.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
              placeholder="Invitation code"
              inputProps={{ autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: false }}
              disabled={busy}
              error={!!error}
              helperText={error || ' '}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={busy || !code.trim()}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Unlock
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default BetaCodeDialog;
